import {useForm} from "@mantine/form";
import {useTranslation} from "react-i18next";
import {Button, Divider, Group, Modal, ModalProps, Stack} from "@mantine/core";
import {useCallback} from "react";
import {
  CompanyEntity,
  getObjectId,
  PersonEntity,
  SiteEntity,
  TrailerEntity,
  VehicleEntity
} from "@ots-frontend/ots-data";
import {useApp} from "../../../providers/RegionProvider/RegionProvider";
import {PersonSelect} from "../../select/entity/PersonSelect/PersonSelect";
import {VehicleSelect} from "../../select/entity/VehicleSelect/VehicleSelect";
import {TrailerSelect} from "../../select/entity/TrailerSelect/TrailerSelect";
import {CompanySelect} from "../../select/entity/CompanySelect/CompanySelect";
import {SiteSelect} from "../../select/entity/SiteSelect/SiteSelect";

interface DispatchFilterFormProps {
  defaultValues?: Partial<DispatchFilterFormValues>;
  onSubmit?: (values: DispatchFilterFormValues) => void;
}

export interface DispatchFilterFormValues {
  person?: PersonEntity | string;
  vehicle?: VehicleEntity | string;
  trailer?: TrailerEntity | string;
  pickupSite?: SiteEntity | string;
  pickupSiteCompany?: CompanyEntity | string;
  deliverySite?: SiteEntity | string;
  deliverySiteCompany?: CompanyEntity | string;
}

export const DEFAULT_DISPATCH_FILTER: DispatchFilterFormValues = {
  person: undefined,
  vehicle: undefined,
  trailer: undefined,
  pickupSite: undefined,
  pickupSiteCompany: undefined,
  deliverySite: undefined,
  deliverySiteCompany: undefined,
}

export const isDefaultDispatchFilter = (filter1: DispatchFilterFormValues): boolean => {
  if (filter1 === DEFAULT_DISPATCH_FILTER) {
    return true
  }

  if (getObjectId(filter1.person) !== getObjectId(DEFAULT_DISPATCH_FILTER.person)) {
    return false;
  }

  if (getObjectId(filter1.vehicle) !== getObjectId(DEFAULT_DISPATCH_FILTER.vehicle)) {
    return false;
  }

  if (getObjectId(filter1.trailer) !== getObjectId(DEFAULT_DISPATCH_FILTER.trailer)) {
    return false;
  }

  if (getObjectId(filter1.pickupSite) !== getObjectId(DEFAULT_DISPATCH_FILTER.pickupSite)) {
    return false;
  }

  if (getObjectId(filter1.pickupSiteCompany) !== getObjectId(DEFAULT_DISPATCH_FILTER.pickupSiteCompany)) {
    return false;
  }

  if (getObjectId(filter1.deliverySite) !== getObjectId(DEFAULT_DISPATCH_FILTER.deliverySite)) {
    return false;
  }

  if (getObjectId(filter1.deliverySiteCompany) !== getObjectId(DEFAULT_DISPATCH_FILTER.deliverySiteCompany)) {
    return false;
  }

  return true;
}

export const DispatchFilterForm = (props: DispatchFilterFormProps) => {
  const {
    defaultValues,
    onSubmit,
  } = props;

  const {t} = useTranslation(["common"]);

  const {regionId} = useApp()

  const form = useForm({
    initialValues: {
      person: getObjectId(defaultValues?.person),
      vehicle: getObjectId(defaultValues?.vehicle),
      trailer: getObjectId(defaultValues?.trailer),
      pickupSite: getObjectId(defaultValues?.pickupSite),
      pickupSiteCompany: getObjectId(defaultValues?.pickupSiteCompany),
      deliverySite: getObjectId(defaultValues?.deliverySite),
      deliverySiteCompany: getObjectId(defaultValues?.deliverySiteCompany),
    },
    validate: {}
  });

  const onFormSubmit = useCallback(async (values: typeof form.values) => {
    if (onSubmit) {
      onSubmit({
        person: values.person,
        vehicle: values.vehicle,
        trailer: values.trailer,
        pickupSite: values.pickupSite,
        pickupSiteCompany: values.pickupSiteCompany,
        deliverySite: values.deliverySite,
        deliverySiteCompany: values.deliverySiteCompany,
      });
    }
  }, [form, onSubmit]);

  const onClearClick = useCallback(() => {
    if (onSubmit) {
      onSubmit(DEFAULT_DISPATCH_FILTER)
    }
  }, [onSubmit])

  return (
    <form onSubmit={form.onSubmit(onFormSubmit)}>
      <Stack spacing="md">
        <PersonSelect
          label={t("common.entity.dispatch.filter.form.field.person", "User")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.person", "User")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("person")} />
        <VehicleSelect
          label={t("common.entity.dispatch.filter.form.field.vehicle", "Truck")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.vehicle", "Truck")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("vehicle")} />
        <TrailerSelect
          label={t("common.entity.dispatch.filter.form.field.trailer", "Trailer")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.trailer", "Trailer")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("trailer")} />
        <Divider/>
        <CompanySelect
          label={t("common.entity.dispatch.filter.form.field.pickupcompany", "Pickup Company")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.pickupcompany", "Pickup Company")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("pickupSiteCompany")} />
        <SiteSelect
          label={t("common.entity.dispatch.filter.form.field.pickupsite", "Pickup Site")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.pickupsite", "Pickup Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("pickupSite")} />
        <Divider/>
        <CompanySelect
          label={t("common.entity.dispatch.filter.form.field.deliverycompany", "Delivery Company")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.deliverycompany", "Delivery Company")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("deliverySiteCompany")} />
        <SiteSelect
          label={t("common.entity.dispatch.filter.form.field.deliverysite", "Delivery Site")}
          placeholder={t<string>("common.entity.dispatch.filter.form.field.deliverysite", "Delivery Site")}
          selector={{
            regionId: regionId,
          }}
          withinPortal={true}
          clearable
          {...form.getInputProps("deliverySite")} />
      </Stack>
      <Group position="center" grow={true}>
        <Button fullWidth mt="xl" onClick={onClearClick}>
          {t("common.action.clear", "Clear", {ns: "common"})}
        </Button>
        <Button type="submit" fullWidth mt="xl">
          {t("common.action.submit", "Submit", {ns: "common"})}
        </Button>
      </Group>
    </form>
  );
};

interface DispatchFilterFormModalProps extends DispatchFilterFormProps, Omit<ModalProps, 'onSubmit'> {

}

export const DispatchFilterFormModal = (props: DispatchFilterFormModalProps) => {
  const {
    defaultValues,
    onSubmit,
    ...rest
  } = props;

  return (
    <Modal {...rest}>
      <DispatchFilterForm defaultValues={defaultValues} onSubmit={onSubmit}/>
    </Modal>
  );
};
